import React, { useMemo, useState, useContext, createElement, Component } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { FileText, Download, Search, Calendar, Filter, Eye, Settings, ChevronDown, Copy, Check, X, Clock, User, AlertCircle, CheckCircle, XCircle, Activity, ArrowRight } from 'lucide-react';
import { AppCard } from '../components/base/AppCard';
import { AppButton } from '../components/base/AppButton';
import { AppBadge } from '../components/base/AppBadge';
import { AppInput } from '../components/base/AppInput';
import { Toast } from '../components/Toast';
import { useAgent } from '../contexts/AgentContext';
// Types
interface AuditLog {
  id: string;
  timestamp: string;
  userId: string;
  userName: string;
  userEmail: string;
  serviceId: string;
  serviceName: string;
  eventType: 'Query' | 'Response' | 'Error' | 'Policy Violation' | 'System Event';
  severity: 'Info' | 'Warning' | 'Critical';
  inputPreview: string;
  outputPreview: string;
  duration: number;
  status: 'Success' | 'Failed' | 'Partial';
  tokenUsage: number;
  cost: number;
}
interface LogDetails extends AuditLog {
  fullInput: string;
  fullOutput: string;
  metadata: {
    modelUsed: string;
    toolsInvoked: string[];
    knowledgeSources: string[];
    requestHeaders?: Record<string, string>;
    ip?: string;
    userAgent?: string;
  };
  trace: Array<{
    step: string;
    timestamp: string;
    duration: number;
    status: string;
  }>;
}
// Helper Functions
function isToday(date: Date): boolean {
  const today = new Date();
  return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
}
function formatDate(timestamp: string): string {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(date);
}
function convertToCSV(logs: AuditLog[]): string {
  const headers = ['ID', 'Timestamp', 'User', 'Email', 'Service', 'Event Type', 'Status', 'Duration (ms)', 'Tokens', 'Cost'];
  const rows = logs.map(log => [log.id, log.timestamp, log.userName, log.userEmail, log.serviceName, log.eventType, log.status, log.duration, log.tokenUsage, log.cost]);
  return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
}
function generateMockLogs(): AuditLog[] {
  const logs: AuditLog[] = [];
  const eventTypes: AuditLog['eventType'][] = ['Query', 'Response', 'Error', 'Policy Violation', 'System Event'];
  const severities: AuditLog['severity'][] = ['Info', 'Warning', 'Critical'];
  const statuses: AuditLog['status'][] = ['Success', 'Failed', 'Partial'];
  const services = ['Service 1', 'Service 2', 'Service 3'];
  const users = [{
    name: 'Mario Rossi',
    email: 'mario.rossi@example.com'
  }, {
    name: 'Laura Bianchi',
    email: 'laura.bianchi@example.com'
  }, {
    name: 'Giuseppe Verdi',
    email: 'giuseppe.verdi@example.com'
  }];
  for (let i = 0; i < 150; i++) {
    const user = users[Math.floor(Math.random() * users.length)];
    const service = services[Math.floor(Math.random() * services.length)];
    const eventType = eventTypes[Math.floor(Math.random() * eventTypes.length)];
    const date = new Date();
    date.setHours(date.getHours() - Math.floor(Math.random() * 72));
    logs.push({
      id: `log-${i + 1}`,
      timestamp: date.toISOString(),
      userId: `user-${Math.floor(Math.random() * 3) + 1}`,
      userName: user.name,
      userEmail: user.email,
      serviceId: `service-${Math.floor(Math.random() * 3) + 1}`,
      serviceName: service,
      eventType,
      severity: severities[Math.floor(Math.random() * severities.length)],
      inputPreview: `Query about ${['product info', 'pricing', 'support', 'features'][Math.floor(Math.random() * 4)]}`,
      outputPreview: `Response with ${['detailed info', 'pricing details', 'support guidance', 'feature list'][Math.floor(Math.random() * 4)]}`,
      duration: Math.floor(Math.random() * 5000) + 100,
      status: statuses[Math.floor(Math.random() * statuses.length)],
      tokenUsage: Math.floor(Math.random() * 2000) + 100,
      cost: Math.random() * 0.5
    });
  }
  return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}
function generateLogDetails(log: AuditLog): LogDetails {
  return {
    ...log,
    fullInput: `Full input text for ${log.inputPreview}. This would contain the complete user query with all context and parameters.`,
    fullOutput: `Full output text for ${log.outputPreview}. This would contain the complete AI response with all details and formatting.`,
    metadata: {
      modelUsed: 'gpt-4-turbo',
      toolsInvoked: ['search', 'calculator', 'database_query'],
      knowledgeSources: ['internal_docs', 'public_api', 'knowledge_base'],
      requestHeaders: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ***'
      },
      ip: '192.168.1.1',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)'
    },
    trace: [{
      step: 'Request Received',
      timestamp: log.timestamp,
      duration: 5,
      status: 'Success'
    }, {
      step: 'Policy Check',
      timestamp: new Date(new Date(log.timestamp).getTime() + 5).toISOString(),
      duration: 15,
      status: 'Success'
    }, {
      step: 'Model Inference',
      timestamp: new Date(new Date(log.timestamp).getTime() + 20).toISOString(),
      duration: log.duration - 50,
      status: 'Success'
    }, {
      step: 'Response Sent',
      timestamp: new Date(new Date(log.timestamp).getTime() + log.duration).toISOString(),
      duration: 10,
      status: 'Success'
    }]
  };
}
// Log Details Modal Component
interface LogDetailsModalProps {
  log: LogDetails;
  activeTab: 'overview' | 'request' | 'response' | 'trace' | 'metadata';
  onTabChange: (tab: 'overview' | 'request' | 'response' | 'trace' | 'metadata') => void;
  onClose: () => void;
}
function LogDetailsModal({
  log,
  activeTab,
  onTabChange,
  onClose
}: LogDetailsModalProps) {
  const [copied, setCopied] = useState(false);
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-1524">
      <div className="bg-[#0a0a0a] rounded-2xl border border-white/[0.08] w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-id="element-1525">
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/[0.06] flex items-center justify-between" data-id="element-1526">
          <div data-id="element-1527">
            <h2 className="text-[17px] font-semibold text-white" data-id="element-1528">
              Log Details
            </h2>
            <p className="text-[12px] text-white/40 mt-1" data-id="element-1529">{log.id}</p>
          </div>
          <button onClick={onClose} className="p-2 text-white\\/40 hover:text-white\\/70 hover:bg-white\\/[0.04] rounded-lg transition-all" data-id="element-1530">
            <X className="w-5 h-5" data-id="element-1531" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-white/[0.06]" data-id="element-1532">
          <div className="flex space-x-1" data-id="element-1533">
            {[{
            id: 'overview',
            label: 'Overview'
          }, {
            id: 'request',
            label: 'Request'
          }, {
            id: 'response',
            label: 'Response'
          }, {
            id: 'trace',
            label: 'Trace'
          }, {
            id: 'metadata',
            label: 'Metadata'
          }].map(tab => <button key={tab.id} onClick={() => onTabChange(tab.id as any)} className={`px-4 py-3 text-[12px] font-medium transition-all relative ${activeTab === tab.id ? 'text-white' : 'text-white/35 hover:text-white/60'}`} data-id="element-1534">
                {tab.label}
                {activeTab === tab.id && <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-[#8eff01] rounded-full" data-id="element-1535" />}
              </button>)}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar" data-id="element-1536">
          {activeTab === 'overview' && <div className="space-y-6" data-id="element-1537">
              <div className="grid grid-cols-2 gap-4" data-id="element-1538">
                <div data-id="element-1539">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1540">Timestamp</p>
                  <p className="text-[12px] text-white" data-id="element-1541">
                    {formatDate(log.timestamp)}
                  </p>
                </div>
                <div data-id="element-1542">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1543">Duration</p>
                  <p className="text-[12px] text-white" data-id="element-1544">{log.duration}ms</p>
                </div>
                <div data-id="element-1545">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1546">User</p>
                  <p className="text-[12px] text-white" data-id="element-1547">{log.userName}</p>
                  <p className="text-[10px] text-white/40" data-id="element-1548">{log.userEmail}</p>
                </div>
                <div data-id="element-1549">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1550">Service</p>
                  <p className="text-[12px] text-white" data-id="element-1551">{log.serviceName}</p>
                </div>
                <div data-id="element-1552">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1553">Event Type</p>
                  <AppBadge variant="blue" data-id="element-1554">{log.eventType}</AppBadge>
                </div>
                <div data-id="element-1555">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1556">Status</p>
                  <AppBadge variant={log.status === 'Success' ? 'green' : log.status === 'Failed' ? 'red' : 'amber'} data-id="element-1557">
                    {log.status}
                  </AppBadge>
                </div>
                <div data-id="element-1558">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1559">Token Usage</p>
                  <p className="text-[12px] text-white" data-id="element-1560">{log.tokenUsage}</p>
                </div>
                <div data-id="element-1561">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1562">Cost</p>
                  <p className="text-[12px] text-white" data-id="element-1563">
                    ${log.cost.toFixed(4)}
                  </p>
                </div>
              </div>
            </div>}

          {activeTab === 'request' && <div data-id="element-1564">
              <div className="flex items-center justify-between mb-4" data-id="element-1565">
                <h3 className="text-[12px] font-semibold text-white/70" data-id="element-1566">
                  Full Request
                </h3>
                <button onClick={() => handleCopy(log.fullInput)} className="flex items-center gap-2 px-3 py-1.5 text-[10px] text-white\\/40 hover:text-white\\/70 bg-white\\/[0.03] hover:bg-white\\/[0.06] rounded-lg transition-all" data-id="element-1567">
                  {copied ? <>
                      <Check className="w-3 h-3" data-id="element-1568" />
                      Copied
                    </> : <>
                      <Copy className="w-3 h-3" data-id="element-1569" />
                      Copy
                    </>}
                </button>
              </div>
              <div className="bg-white/[0.03] rounded-lg p-4 border border-white/[0.08]" data-id="element-1570">
                <pre className="text-[12px] text-white/70 whitespace-pre-wrap font-mono" data-id="element-1571">
                  {log.fullInput}
                </pre>
              </div>
            </div>}

          {activeTab === 'response' && <div data-id="element-1572">
              <div className="flex items-center justify-between mb-4" data-id="element-1573">
                <h3 className="text-[12px] font-semibold text-white/70" data-id="element-1574">
                  Full Response
                </h3>
                <button onClick={() => handleCopy(log.fullOutput)} className="flex items-center gap-2 px-3 py-1.5 text-[10px] text-white\\/40 hover:text-white\\/70 bg-white\\/[0.03] hover:bg-white\\/[0.06] rounded-lg transition-all" data-id="element-1575">
                  {copied ? <>
                      <Check className="w-3 h-3" data-id="element-1576" />
                      Copied
                    </> : <>
                      <Copy className="w-3 h-3" data-id="element-1577" />
                      Copy
                    </>}
                </button>
              </div>
              <div className="bg-white/[0.03] rounded-lg p-4 border border-white/[0.08]" data-id="element-1578">
                <pre className="text-[12px] text-white/70 whitespace-pre-wrap font-mono" data-id="element-1579">
                  {log.fullOutput}
                </pre>
              </div>
            </div>}

          {activeTab === 'trace' && <div className="space-y-3" data-id="element-1580">
              {log.trace.map((step, index) => <div key={index} className="flex items-start gap-4 p-4 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-1581">
                  <div className="flex-shrink-0" data-id="element-1582">
                    {step.status === 'Success' ? <CheckCircle className="w-5 h-5 text-[#8eff01]" data-id="element-1583" /> : <XCircle className="w-5 h-5 text-rose-400" data-id="element-1584" />}
                  </div>
                  <div className="flex-1" data-id="element-1585">
                    <div className="flex items-center justify-between mb-2" data-id="element-1586">
                      <h4 className="text-[12px] font-medium text-white" data-id="element-1587">
                        {step.step}
                      </h4>
                      <span className="text-[10px] text-white/40" data-id="element-1588">
                        {step.duration}ms
                      </span>
                    </div>
                    <p className="text-[10px] text-white/40" data-id="element-1589">
                      {formatDate(step.timestamp)}
                    </p>
                  </div>
                </div>)}
            </div>}

          {activeTab === 'metadata' && <div className="space-y-6" data-id="element-1590">
              <div data-id="element-1591">
                <h3 className="text-[12px] font-semibold text-white/70 mb-3" data-id="element-1592">
                  Model Information
                </h3>
                <div className="bg-white/[0.03] rounded-lg p-4 border border-white/[0.08]" data-id="element-1593">
                  <p className="text-[12px] text-white" data-id="element-1594">
                    {log.metadata.modelUsed}
                  </p>
                </div>
              </div>

              <div data-id="element-1595">
                <h3 className="text-[12px] font-semibold text-white/70 mb-3" data-id="element-1596">
                  Tools Invoked
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-1597">
                  {log.metadata.toolsInvoked.map(tool => <AppBadge key={tool} variant="blue" data-id="element-1598">
                      {tool}
                    </AppBadge>)}
                </div>
              </div>

              <div data-id="element-1599">
                <h3 className="text-[12px] font-semibold text-white/70 mb-3" data-id="element-1600">
                  Knowledge Sources
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-1601">
                  {log.metadata.knowledgeSources.map(source => <AppBadge key={source} variant="default" data-id="element-1602">
                      {source}
                    </AppBadge>)}
                </div>
              </div>

              {log.metadata.ip && <div data-id="element-1603">
                  <h3 className="text-[12px] font-semibold text-white/70 mb-3" data-id="element-1604">
                    Request Info
                  </h3>
                  <div className="space-y-2" data-id="element-1605">
                    <div className="flex items-center justify-between p-3 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-1606">
                      <span className="text-[12px] text-white/40" data-id="element-1607">
                        IP Address
                      </span>
                      <span className="text-[12px] text-white" data-id="element-1608">
                        {log.metadata.ip}
                      </span>
                    </div>
                    {log.metadata.userAgent && <div className="p-3 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-1609">
                        <p className="text-[12px] text-white/40 mb-1" data-id="element-1610">
                          User Agent
                        </p>
                        <p className="text-[10px] text-white/70 break-all" data-id="element-1611">
                          {log.metadata.userAgent}
                        </p>
                      </div>}
                  </div>
                </div>}
            </div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/[0.06] flex justify-end" data-id="element-1612">
          <AppButton variant="secondary" onClick={onClose} data-id="element-1613">
            Close
          </AppButton>
        </div>
      </div>
    </div>;
}
// Retention Policy Modal Component
interface RetentionPolicyModalProps {
  onClose: () => void;
  onSave: () => void;
}
function RetentionPolicyModal({
  onClose,
  onSave
}: RetentionPolicyModalProps) {
  const [retentionDays, setRetentionDays] = useState('90');
  const [autoArchive, setAutoArchive] = useState(true);
  const [archiveLocation, setArchiveLocation] = useState('s3');
  const handleSave = () => {
    onSave();
  };
  return <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-1614">
      <div className="bg-[#0a0a0a] rounded-2xl border border-white/[0.08] w-full max-w-2xl" data-id="element-1615">
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/[0.06] flex items-center justify-between" data-id="element-1616">
          <div data-id="element-1617">
            <h2 className="text-[17px] font-semibold text-white" data-id="element-1618">
              Retention Policy
            </h2>
            <p className="text-[12px] text-white/40 mt-1" data-id="element-1619">
              Configura la policy di conservazione dei log
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-white\\/40 hover:text-white\\/70 hover:bg-white\\/[0.04] rounded-lg transition-all" data-id="element-1620">
            <X className="w-5 h-5" data-id="element-1621" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6" data-id="element-1622">
          <div data-id="element-1623">
            <label className="block text-[12px] font-medium text-white/70 mb-2" data-id="element-1624">
              Periodo di Conservazione (giorni)
            </label>
            <AppInput type="number" value={retentionDays} onChange={e => setRetentionDays(e.target.value)} placeholder="90" data-id="element-1625" />
            <p className="text-[10px] text-white/40 mt-2" data-id="element-1626">
              I log più vecchi di questo periodo verranno automaticamente
              archiviati
            </p>
          </div>

          <div className="flex items-center justify-between p-4 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-1627">
            <div data-id="element-1628">
              <p className="text-[12px] font-medium text-white" data-id="element-1629">
                Archiviazione Automatica
              </p>
              <p className="text-[10px] text-white/40 mt-1" data-id="element-1630">
                Archivia automaticamente i log scaduti
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-1631">
              <input type="checkbox" checked={autoArchive} onChange={e => setAutoArchive(e.target.checked)} className="sr-only peer" data-id="element-1632" />
              <div className="w-11 h-6 bg-white\\/[0.08] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-\\[#8eff01]\\/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-\\[#8eff01]\\" data-id="element-1633"></div>
            </label>
          </div>

          {autoArchive && <div data-id="element-1634">
              <label className="block text-[12px] font-medium text-white/70 mb-2" data-id="element-1635">
                Destinazione Archivio
              </label>
              <select value={archiveLocation} onChange={e => setArchiveLocation(e.target.value)} className="w-full bg-white\\/[0.03] border border-white\\/[0.08] text-white px-4 py-2.5 rounded-lg text-[12px] focus:outline-none focus:border-\\[#8eff01]\\/50" data-id="element-1636">
                <option value="s3" data-id="element-1637">Amazon S3</option>
                <option value="azure" data-id="element-1638">Azure Blob Storage</option>
                <option value="gcs" data-id="element-1639">Google Cloud Storage</option>
                <option value="local" data-id="element-1640">Local Storage</option>
              </select>
            </div>}

          <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl" data-id="element-1641">
            <div className="flex items-start gap-3" data-id="element-1642">
              <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" data-id="element-1643" />
              <div data-id="element-1644">
                <p className="text-[12px] font-medium text-white mb-1" data-id="element-1645">
                  Attenzione
                </p>
                <p className="text-[12px] text-white/40" data-id="element-1646">
                  La modifica della retention policy potrebbe influenzare la
                  conformità normativa. Assicurati di rispettare i requisiti
                  GDPR e AI ACT.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/[0.06] flex justify-end gap-3" data-id="element-1647">
          <AppButton variant="secondary" onClick={onClose} data-id="element-1648">
            Annulla
          </AppButton>
          <AppButton variant="primary" onClick={handleSave} data-id="element-1649">
            Salva Policy
          </AppButton>
        </div>
      </div>
    </div>;
}
// Main Component
interface AuditTrailPageProps {
  hideHeader?: boolean;
  serviceScope?: string | null;
}
export function AuditTrailPage({
  hideHeader = false,
  serviceScope
}: AuditTrailPageProps) {
  // Context
  const {
    selectedServiceId,
    setSelectedServiceId
  } = useAgent();
  const location = useLocation();
  // Usa serviceScope da prop, oppure da context
  const effectiveServiceScope = serviceScope !== undefined ? serviceScope : selectedServiceId;
  const isServiceScoped = !!effectiveServiceScope;
  // Helper function to get service name
  const getServiceName = (serviceId: string | null) => {
    if (!serviceId) return 'Unknown';
    const serviceMap: Record<string, string> = {
      'service-1': 'Service 1',
      'service-2': 'Service 2',
      'service-3': 'Service 3',
      'service-4': 'Service 4',
      'service-5': 'Service 5'
    };
    return serviceMap[serviceId] || `Service ${serviceId}`;
  };
  // State
  const [allLogs] = useState<AuditLog[]>(generateMockLogs());
  const [selectedLog, setSelectedLog] = useState<LogDetails | null>(null);
  const [showRetentionModal, setShowRetentionModal] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState<'overview' | 'request' | 'response' | 'trace' | 'metadata'>('overview');
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedEventType, setSelectedEventType] = useState<string>('All');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('All');
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const logsPerPage = 50;
  // Filtered logs with SERVICE SCOPE as primary filter
  const filteredLogs = useMemo(() => {
    return allLogs.filter(log => {
      // PRIMARY FILTER: Service scope
      if (isServiceScoped && effectiveServiceScope) {
        if (log.serviceId !== effectiveServiceScope) return false;
      }
      // Secondary filters (esistenti)
      if (searchQuery && !log.inputPreview.toLowerCase().includes(searchQuery.toLowerCase()) && !log.outputPreview.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (selectedEventType !== 'All' && log.eventType !== selectedEventType) return false;
      if (selectedSeverity !== 'All' && log.severity !== selectedSeverity) return false;
      if (selectedServices.length > 0 && !selectedServices.includes(log.serviceId)) return false;
      if (selectedUsers.length > 0 && !selectedUsers.includes(log.userId)) return false;
      return true;
    });
  }, [allLogs, isServiceScoped, effectiveServiceScope, searchQuery, selectedEventType, selectedSeverity, selectedServices, selectedUsers]);
  // Paginated logs
  const paginatedLogs = useMemo(() => {
    const startIndex = (currentPage - 1) * logsPerPage;
    return filteredLogs.slice(startIndex, startIndex + logsPerPage);
  }, [filteredLogs, currentPage]);
  // Stats
  const stats = useMemo(() => ({
    totalLogs: filteredLogs.length,
    todayActivity: filteredLogs.filter(l => isToday(new Date(l.timestamp))).length,
    avgResponseTime: filteredLogs.length > 0 ? (filteredLogs.reduce((sum, l) => sum + l.duration, 0) / filteredLogs.length / 1000).toFixed(1) : '0.0',
    complianceScore: 98
  }), [filteredLogs]);
  // Handlers
  const handleExport = (format: 'csv' | 'json') => {
    const dataStr = format === 'json' ? JSON.stringify(filteredLogs, null, 2) : convertToCSV(filteredLogs);
    const blob = new Blob([dataStr], {
      type: format === 'json' ? 'application/json' : 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-trail-${new Date().toISOString().split('T')[0]}.${format}`;
    link.click();
    setToast({
      message: `Export ${format.toUpperCase()} completato`,
      type: 'success'
    });
  };
  const handleViewDetails = (log: AuditLog) => {
    setSelectedLog(generateLogDetails(log));
  };
  const handleResetFilters = () => {
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
    setSelectedServices([]);
    setSelectedUsers([]);
    setSelectedEventType('All');
    setSelectedSeverity('All');
  };
  return <div className="min-h-screen bg-[#121212]" data-id="element-1650">
      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} isVisible={!!toast} onClose={() => setToast(null)} data-id="element-1651" />}

      {/* Header */}
      {!hideHeader && <div className="pt-6 px-4 md:px-8 pb-4" data-id="element-1652">
          <h1 className="text-[20px] font-semibold text-white tracking-tight" data-id="element-1653">
            Audit Trail
          </h1>
          <p className="text-[13px] text-white/40 mt-1" data-id="element-1654">
            Log immutabile delle interazioni AI
          </p>
        </div>}

      <div className="px-4 md:px-8 space-y-6 pb-12" data-id="element-1655">
        {/* Service-Scoped Banner */}
        {isServiceScoped && effectiveServiceScope && <div className="bg-[#8eff01]/5 border border-[#8eff01]/20 rounded-xl p-4 flex items-center justify-between" data-id="element-1656">
            <div className="flex items-center gap-3" data-id="element-1657">
              <div className="p-2 bg-[#8eff01]/10 rounded-lg" data-id="element-1658">
                <Filter className="w-5 h-5 text-[#8eff01]" data-id="element-1659" />
              </div>
              <div data-id="element-1660">
                <p className="text-[12px] font-medium text-white" data-id="element-1661">
                  Viewing: {getServiceName(effectiveServiceScope)}
                </p>
                <p className="text-[10px] text-white/40 mt-0.5" data-id="element-1662">
                  Showing data for this service only
                </p>
              </div>
            </div>
            <Link to="/audit-trail" onClick={() => setSelectedServiceId(null)} className="flex items-center gap-2 px-4 py-2 text-[12px] font-medium text-\\[#8eff01\\] hover:text-\\[#8eff01\\] hover:bg-\\[#8eff01\\]\\/10 rounded-lg transition-all" data-id="element-1663">
              View all services
              <ArrowRight className="w-4 h-4" data-id="element-1664" />
            </Link>
          </div>}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-1665">
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm" data-id="element-1666">
            <p className="text-[24px] font-semibold text-[#1A202C]" data-id="element-1667">
              {stats.totalLogs.toLocaleString()}
            </p>
            <p className="text-[11px] text-gray-500 mt-1 uppercase tracking-wider" data-id="element-1668">
              Total Logs
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm" data-id="element-1669">
            <p className="text-[24px] font-semibold text-[#1A202C]" data-id="element-1670">
              {stats.todayActivity}
            </p>
            <p className="text-[11px] text-gray-500 mt-1 uppercase tracking-wider" data-id="element-1671">
              Today's Activity
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm" data-id="element-1672">
            <p className="text-[24px] font-semibold text-[#1A202C]" data-id="element-1673">
              {stats.avgResponseTime}s
            </p>
            <p className="text-[11px] text-gray-500 mt-1 uppercase tracking-wider" data-id="element-1674">
              Avg Response Time
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm" data-id="element-1675">
            <p className="text-[24px] font-semibold text-[#00B4D8]" data-id="element-1676">
              {stats.complianceScore}%
            </p>
            <p className="text-[11px] text-gray-500 mt-1 uppercase tracking-wider" data-id="element-1677">
              Compliance Score
            </p>
          </div>
        </div>

        {/* Compact Filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between" data-id="element-1678">
          <div className="flex flex-1 items-center gap-4 w-full md:w-auto" data-id="element-1679">
            <div className="relative flex-1 md:max-w-xs" data-id="element-1680">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" data-id="element-1681" />
              <input type="text" placeholder="Cerca..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-gray-50 border border-gray-200 text-[#1A202C] pl-9 pr-4 py-2.5 rounded-lg text-[12px] placeholder-gray-400 focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8]" data-id="element-1682" />
            </div>

            <select value={selectedEventType} onChange={e => setSelectedEventType(e.target.value)} className="bg-gray-50 border border-gray-200 text-[#1A202C] px-4 py-2.5 rounded-lg text-[12px] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8]" data-id="element-1683">
              <option data-id="element-1684">All Events</option>
              <option data-id="element-1685">Query</option>
              <option data-id="element-1686">Response</option>
              <option data-id="element-1687">Error</option>
              <option data-id="element-1688">Policy Violation</option>
              <option data-id="element-1689">System Event</option>
            </select>

            <select value={selectedSeverity} onChange={e => setSelectedSeverity(e.target.value)} className="bg-gray-50 border border-gray-200 text-[#1A202C] px-4 py-2.5 rounded-lg text-[12px] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8]" data-id="element-1690">
              <option data-id="element-1691">All Severities</option>
              <option data-id="element-1692">Info</option>
              <option data-id="element-1693">Warning</option>
              <option data-id="element-1694">Critical</option>
            </select>
          </div>

          <div className="flex gap-2" data-id="element-1695">
            <button onClick={() => handleExport('csv')} title="Export CSV" className="p-2 text-gray-600 hover:text-[#1A202C] hover:bg-gray-100 rounded-lg transition-all" data-id="element-1696">
              <Download className="w-4 h-4" data-id="element-1697" />
            </button>
            <button onClick={handleResetFilters} title="Reset Filters" className="p-2 text-gray-600 hover:text-[#1A202C] hover:bg-gray-100 rounded-lg transition-all" data-id="element-1698">
              <X className="w-4 h-4" data-id="element-1699" />
            </button>
          </div>
        </div>

        {/* Audit Table */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-1700">
          <div className="overflow-x-auto" data-id="element-1701">
            <table className="w-full" data-id="element-1702">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-1703">
                <tr data-id="element-1704">
                  <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-1705">
                    Timestamp
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-1706">
                    User
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-1707">
                    Service
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-1708">
                    Event Type
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-1709">
                    Status
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-1710">
                    Duration
                  </th>
                  <th className="px-5 py-3 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-1711">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-1712">
                {paginatedLogs.map(log => <tr key={log.id} className="hover:bg-gray-50 transition-colors" data-id="element-1713">
                    <td className="px-5 py-4 whitespace-nowrap text-[12px] text-gray-700" data-id="element-1714">
                      {formatDate(log.timestamp)}
                    </td>
                    <td className="px-5 py-4" data-id="element-1715">
                      <div data-id="element-1716">
                        <p className="text-[12px] font-medium text-[#1A202C]" data-id="element-1717">
                          {log.userName}
                        </p>
                        <p className="text-[10px] text-gray-500" data-id="element-1718">
                          {log.userEmail}
                        </p>
                      </div>
                    </td>
                    <td className="px-5 py-4" data-id="element-1719">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200" data-id="element-1720">
                        {log.serviceName}
                      </span>
                    </td>
                    <td className="px-5 py-4" data-id="element-1721">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${log.eventType === 'Query' ? 'bg-blue-100 text-blue-800 border border-blue-200' : log.eventType === 'Response' ? 'bg-green-100 text-green-800 border border-green-200' : log.eventType === 'Error' || log.eventType === 'Policy Violation' ? 'bg-red-100 text-red-800 border border-red-200' : 'bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-1722">
                        {log.eventType}
                      </span>
                    </td>
                    <td className="px-5 py-4" data-id="element-1723">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${log.status === 'Success' ? 'bg-green-100 text-green-800 border border-green-200' : log.status === 'Failed' ? 'bg-red-100 text-red-800 border border-red-200' : 'bg-amber-100 text-amber-800 border border-amber-200'}`} data-id="element-1724">
                        {log.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-[12px] text-gray-700" data-id="element-1725">
                      {log.duration}ms
                    </td>
                    <td className="px-5 py-4 text-right" data-id="element-1726">
                      <button onClick={() => handleViewDetails(log)} className="p-2 text-gray-600 hover:text-[#00B4D8] hover:bg-gray-100 rounded-lg transition-all" data-id="element-1727">
                        <Eye className="w-4 h-4" data-id="element-1728" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-1729">
            <p className="text-[12px] text-gray-600" data-id="element-1730">
              Showing {(currentPage - 1) * logsPerPage + 1}-
              {Math.min(currentPage * logsPerPage, filteredLogs.length)} of{' '}
              {filteredLogs.length} logs
            </p>
            <div className="flex gap-2" data-id="element-1731">
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="px-3 py-1.5 text-[12px] font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all" data-id="element-1732">
                Previous
              </button>
              <button disabled={currentPage * logsPerPage >= filteredLogs.length} onClick={() => setCurrentPage(p => p + 1)} className="px-3 py-1.5 text-[12px] font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all" data-id="element-1733">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
  {
    /* Floating Action Button */
  }
  {
    /* Log Details Modal */
  }
  {
    selectedLog && <LogDetailsModal log={selectedLog} activeTab={activeDetailTab} onTabChange={setActiveDetailTab} onClose={() => setSelectedLog(null)} data-id="element-1734" />;
  }
  {
    /* Retention Policy Modal */
  }
  {
    showRetentionModal && <RetentionPolicyModal onClose={() => setShowRetentionModal(false)} onSave={() => {
      setShowRetentionModal(false);
      setToast({
        message: 'Retention policy salvata',
        type: 'success'
      });
    }} data-id="element-1735" />;
  }
}